import { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaysafeManagement = () => {
  const { token } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchReceipts();
  }, [filter]);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/paysafe/receipts?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data);
    } catch (error) {
      toast.error('Failed to fetch receipts');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(
        `${API}/admin/paysafe/${selectedReceipt.order_id}/approve`,
        { notes: approvalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment approved! Customer will be notified via email.');
      setShowApprovalModal(false);
      setApprovalNotes('');
      fetchReceipts();
    } catch (error) {
      toast.error('Failed to approve payment');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Paysafe Payment Verifications</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={filter === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilter('pending')}
              >
                Pending
              </Button>
              <Button
                size="sm"
                variant={filter === 'approved' ? 'default' : 'outline'}
                onClick={() => setFilter('approved')}
              >
                Approved
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          ) : receipts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No {filter} receipts</p>
            </div>
          ) : (
            <div className="space-y-4">
              {receipts.map((receipt) => (
                <div key={receipt.id} className="border rounded-lg p-6" data-testid={`receipt-${receipt.order_id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {receipt.status === 'pending' && <Clock className="h-5 w-5 text-orange-600" />}
                        {receipt.status === 'approved' && <CheckCircle className="h-5 w-5 text-green-600" />}
                        {receipt.status === 'rejected' && <XCircle className="h-5 w-5 text-red-600" />}
                        <span className="font-bold">Order {receipt.order_id.substring(0, 8)}...</span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Customer:</strong> {receipt.user_email}</p>
                        {receipt.customer_name && <p><strong>Name:</strong> {receipt.customer_name}</p>}
                        <p><strong>Amount:</strong> ${receipt.total_amount?.toFixed(2) || receipt.amount?.toFixed(2) || 'N/A'}</p>
                        {receipt.paysafe_reference && (
                          <p><strong>Reference:</strong> <code className="bg-gray-100 px-1 rounded text-xs">{receipt.paysafe_reference}</code></p>
                        )}
                        {receipt.billing_address && <p><strong>Address:</strong> {receipt.billing_address}</p>}
                        <p><strong>Submitted:</strong> {new Date(receipt.uploaded_at || receipt.order_created).toLocaleString()}</p>
                        {receipt.verified_at && (
                          <p><strong>Verified:</strong> {new Date(receipt.verified_at).toLocaleString()}</p>
                        )}
                        {receipt.admin_notes && (
                          <p className="mt-2 p-2 bg-gray-50 rounded text-xs">
                            <strong>Notes:</strong> {receipt.admin_notes}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <span className={`px-3 py-1 rounded text-xs font-medium text-center ${
                        receipt.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                        receipt.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {receipt.status}
                      </span>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(receipt.receipt_url, '_blank')}
                          data-testid={`view-receipt-${receipt.order_id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Receipt
                        </Button>

                        {receipt.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedReceipt(receipt);
                              setApprovalNotes('');
                              setShowApprovalModal(true);
                            }}
                            data-testid={`approve-${receipt.order_id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approval Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Paysafe Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded">
              <p className="text-sm font-medium">Order: {selectedReceipt?.order_id.substring(0, 8)}...</p>
              <p className="text-sm">Customer: {selectedReceipt?.user_email}</p>
              <p className="text-sm">Amount: ${selectedReceipt?.total_amount?.toFixed(2)}</p>
            </div>

            <div>
              <Label>Verification Notes (Optional)</Label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes about this verification..."
                className="mt-1"
              />
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded">
              <p className="text-sm font-medium text-green-800">What happens after approval:</p>
              <ul className="text-sm text-green-700 mt-2 space-y-1 list-disc list-inside">
                <li>Order payment status → Paid</li>
                <li>Email notification sent to customer</li>
                <li>Order enters 36-hour shipping queue</li>
                <li>Maksura tracking activated</li>
              </ul>
            </div>

            <Button onClick={handleApprove} className="w-full">
              Approve Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaysafeManagement;
